/**
 * ManageTechnologiesDialog
 *
 * Post-audit-creation technology lifecycle UI (Sprint A).
 *
 * Contract with the backend (`server.py`):
 *   - POST   /audits/{id}/technologies                          {technology_id}
 *   - DELETE /audits/{id}/technologies/{tech_id}                {note?}
 *   - POST   /audits/{id}/technologies/{tech_id}/reactivate
 *   - GET    /audits/{id}/technologies/impact-preview?technology_id=...
 *
 * Design rules (locked with product owner):
 *   1. Retire = soft-delete only. Every retire is reversible via Reactivate.
 *   2. No notifications on any action. New controls appear as Unassigned.
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Loader2, Server, Plus, Trash2, Undo2, AlertTriangle, Search } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function ManageTechnologiesDialog({
  open,
  onClose,
  audit,                 // full audit object
  technologyMap,         // { [tech_id]: { name, category, emoji } }
  allTechnologies,       // [{ id, name, emoji, technologies: [{id, name, ...}] }]
  onChanged,             // async callback — parent refetches audit + controls
}) {
  const [tab, setTab] = useState('active');
  const [busyTechId, setBusyTechId] = useState(null);
  const [addingTechId, setAddingTechId] = useState(null);
  const [addProgress, setAddProgress] = useState(null); // {done, total, added, tech_name}
  const [confirmingRetire, setConfirmingRetire] = useState(null); // {tech_id, preview}
  const [retireNote, setRetireNote] = useState('');
  const [retireConfirm, setRetireConfirm] = useState('');
  const retireConfirmed = retireConfirm.trim().toLowerCase() === 'retire';
  const [addSearch, setAddSearch] = useState('');

  const activeTechIds = audit?.technologies || [];
  const retiredList = audit?.retired_technologies || [];
  const retiredTechIdSet = new Set(retiredList.map(r => r.technology_id));

  const availableToAdd = useMemo(() => {
    // Flatten catalog, exclude anything already active
    const flat = [];
    (allTechnologies || []).forEach(cat => {
      (cat.technologies || []).forEach(t => {
        if (!activeTechIds.includes(t.id)) {
          flat.push({
            ...t,
            categoryName: cat.name,
            categoryEmoji: cat.emoji,
            isRetiredHere: retiredTechIdSet.has(t.id),
          });
        }
      });
    });
    const q = addSearch.trim().toLowerCase();
    if (!q) return flat;
    return flat.filter(t =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.categoryName || '').toLowerCase().includes(q)
    );
  }, [allTechnologies, activeTechIds, retiredTechIdSet, addSearch]);

  const handleAdd = async (techId) => {
    setAddingTechId(techId);
    setAddProgress({ done: 0, total: 0, added: 0, tech_name: '' });
    try {
      const start = await axios.post(
        `${API}/audits/${audit.id}/technologies`,
        { technology_id: techId },
        { headers: authHeaders() },
      );
      const opId = start.data?.operation_id;
      if (!opId) {
        toast.error('Could not start add-technology operation');
        return;
      }
      // Poll every ~2s until completed or failed
      // Safety cap: 10 minutes of polling max.
      const deadline = Date.now() + 10 * 60 * 1000;
      let final = null;
      while (Date.now() <= deadline) {
        await new Promise((r) => setTimeout(r, 2000));
        let st;
        try {
          st = await axios.get(
            `${API}/audits/${audit.id}/technologies/operation/${opId}`,
            { headers: authHeaders() },
          );
        } catch (e) {
          // Transient poll error — keep trying until deadline
          continue;
        }
        const d = st.data || {};
        setAddProgress({
          done: d.categories_done || 0,
          total: d.categories_total || 0,
          added: d.added || 0,
          tech_name: d.technology_name || '',
        });
        if (d.status === 'completed' || d.status === 'failed') {
          final = d;
          break;
        }
      }
      if (!final && Date.now() > deadline) {
        toast.error('Add is taking longer than expected — check back later');
      }
      if (final?.status === 'completed') {
        toast.success(`Added ${final.technology_name} — ${final.added} new controls across ${final.categories_processed} categories.`);
        setTab('active');
      } else if (final?.status === 'failed') {
        toast.error(final.error || 'Add failed. Retry is safe — already-created controls will be skipped.');
      }
      await onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add technology');
    } finally {
      setAddingTechId(null);
      setAddProgress(null);
    }
  };

  const openRetireConfirm = async (techId) => {
    setBusyTechId(techId);
    try {
      const r = await axios.get(
        `${API}/audits/${audit.id}/technologies/impact-preview`,
        { params: { technology_id: techId }, headers: authHeaders() },
      );
      setConfirmingRetire({ tech_id: techId, preview: r.data });
      setRetireNote('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not compute retire impact');
    } finally {
      setBusyTechId(null);
    }
  };

  const handleRetireConfirm = async () => {
    if (!confirmingRetire) return;
    const techId = confirmingRetire.tech_id;
    setBusyTechId(techId);
    try {
      const r = await axios.delete(
        `${API}/audits/${audit.id}/technologies/${techId}`,
        { data: { note: retireNote || null }, headers: authHeaders() },
      );
      const soft = r.data?.controls_soft_deleted ?? 0;
      const strip = r.data?.controls_tag_stripped ?? 0;
      toast.success(`Retired — ${soft} controls archived, ${strip} untagged. Reversible from Retired tab.`);
      setConfirmingRetire(null);
      setRetireNote('');
      setRetireConfirm('');
      await onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to retire technology');
    } finally {
      setBusyTechId(null);
    }
  };

  const handleReactivate = async (techId) => {
    setBusyTechId(techId);
    try {
      const r = await axios.post(
        `${API}/audits/${audit.id}/technologies/${techId}/reactivate`,
        {},
        { headers: authHeaders() },
      );
      const restored = r.data?.controls_restored ?? 0;
      const retagged = r.data?.controls_retagged ?? 0;
      toast.success(`Reactivated — ${restored} controls restored, ${retagged} re-tagged.`);
      await onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reactivate technology');
    } finally {
      setBusyTechId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] !flex flex-col overflow-hidden" data-testid="manage-technologies-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Server className="w-5 h-5 text-slate-700" />
            Manage Technologies
          </DialogTitle>
          <DialogDescription>
            Add technologies to generate new controls, retire the ones no longer required, or reactivate previously retired ones. Retire is always reversible — evidence is preserved.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full flex-1 min-h-0 flex flex-col">
          <TabsList className="grid w-full grid-cols-3 shrink-0">
            <TabsTrigger value="active" data-testid="manage-tech-tab-active">Active ({activeTechIds.length})</TabsTrigger>
            <TabsTrigger value="retired" data-testid="manage-tech-tab-retired">Retired ({retiredList.length})</TabsTrigger>
            <TabsTrigger value="add" data-testid="manage-tech-tab-add">Add</TabsTrigger>
          </TabsList>

          {/* --- Active tab --- */}
          <TabsContent value="active" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1">
            {activeTechIds.length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">No technologies attached to this audit yet.</div>
            )}
            <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white">
              {activeTechIds.map((id) => {
                const info = technologyMap[id] || { name: id, category: '' };
                return (
                  <li key={id} className="flex items-center justify-between px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">
                        {info.emoji ? <span className="mr-1">{info.emoji}</span> : null}
                        {info.name}
                      </div>
                      {info.category && (
                        <div className="text-xs text-slate-500 truncate">{info.category}</div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openRetireConfirm(id)}
                      disabled={busyTechId === id}
                      data-testid={`retire-tech-${id}`}
                    >
                      {busyTechId === id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1 text-rose-600" />}
                      Retire
                    </Button>
                  </li>
                );
              })}
            </ul>
          </TabsContent>

          {/* --- Retired tab --- */}
          <TabsContent value="retired" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1">
            {retiredList.length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">No retired technologies. Retire an active one and it will show up here for one-click undo.</div>
            )}
            <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white">
              {retiredList.map((r) => {
                const info = technologyMap[r.technology_id] || { name: r.technology_id, category: '' };
                return (
                  <li key={r.technology_id} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">
                          {info.emoji ? <span className="mr-1">{info.emoji}</span> : null}
                          {info.name}
                          <Badge variant="secondary" className="ml-2 text-[10px] uppercase tracking-wide">Retired</Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {r.retired_at ? new Date(r.retired_at).toLocaleString() : ''} · {r.retired_by_email || 'unknown'} ·{' '}
                          {(r.soft_deleted_control_ids?.length || 0)} archived / {(r.stripped_from_control_ids?.length || 0)} un-tagged
                        </div>
                        {r.note && <div className="text-xs text-slate-600 italic mt-1">“{r.note}”</div>}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleReactivate(r.technology_id)}
                        disabled={busyTechId === r.technology_id}
                        data-testid={`reactivate-tech-${r.technology_id}`}
                      >
                        {busyTechId === r.technology_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Undo2 className="w-3.5 h-3.5 mr-1" />}
                        Reactivate
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </TabsContent>

          {/* --- Add tab --- */}
          <TabsContent value="add" className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1">
            {addingTechId && addProgress && (
              <div
                className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3"
                data-testid="add-tech-progress"
              >
                <div className="flex items-center justify-between text-sm text-slate-800">
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-slate-600" />
                    Generating controls
                    {addProgress.tech_name ? <span className="font-medium"> · {addProgress.tech_name}</span> : null}
                  </span>
                  <span className="text-xs text-slate-500">
                    {addProgress.total > 0
                      ? `${addProgress.done}/${addProgress.total} categories`
                      : 'Preparing…'}
                  </span>
                </div>
                <div className="mt-2 h-1.5 w-full bg-slate-200 rounded overflow-hidden">
                  <div
                    className="h-full bg-slate-800 transition-all"
                    style={{
                      width: addProgress.total > 0
                        ? `${Math.min(100, (addProgress.done / addProgress.total) * 100)}%`
                        : '10%',
                    }}
                  />
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {addProgress.added} control{addProgress.added === 1 ? '' : 's'} created so far · safe to leave this open
                </div>
              </div>
            )}
            <div className="mb-3 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={addSearch}
                onChange={(e) => setAddSearch(e.target.value)}
                placeholder="Search technologies…"
                className="pl-9"
                data-testid="add-tech-search"
              />
            </div>
            {availableToAdd.length === 0 && (
              <div className="text-sm text-slate-500 py-8 text-center">
                {addSearch ? 'No technologies match your search.' : 'All available technologies are already attached to this audit.'}
              </div>
            )}
            <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white">
              {availableToAdd.map((t) => (
                <li key={t.id} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {t.categoryEmoji ? <span className="mr-1">{t.categoryEmoji}</span> : null}
                      {t.name}
                      {t.isRetiredHere && (
                        <Badge variant="secondary" className="ml-2 text-[10px] uppercase tracking-wide">Previously retired</Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{t.categoryName}</div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleAdd(t.id)}
                    disabled={addingTechId != null}
                    data-testid={`add-tech-${t.id}`}
                  >
                    {addingTechId === t.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                    {addingTechId === t.id ? 'Generating…' : (t.isRetiredHere ? 'Reactivate & regenerate' : 'Add')}
                  </Button>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500 mt-2">
              Adding a technology runs AI generation for every existing category in the background — safe to close and check back. Retries are safe: already-created controls are skipped. Owners are not auto-notified.
            </p>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4 shrink-0">
          <Button variant="outline" onClick={onClose} data-testid="manage-tech-close">Close</Button>
        </DialogFooter>

        {/* --- Retire confirmation nested dialog --- */}
        <Dialog open={!!confirmingRetire} onOpenChange={(v) => { if (!v) { setConfirmingRetire(null); setRetireConfirm(''); } }}>
          <DialogContent className="max-w-md" data-testid="retire-tech-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" /> Retire technology?
              </DialogTitle>
              <DialogDescription>
                This is reversible from the Retired tab. Evidence, comments, drafts and owner assignments are preserved.
              </DialogDescription>
            </DialogHeader>
            {confirmingRetire && (
              <div className="text-sm space-y-2">
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                  <div><strong>{confirmingRetire.preview?.controls_soft_deleted ?? 0}</strong> control(s) will be moved to <em>Deleted Controls</em> (archived).</div>
                  <div><strong>{confirmingRetire.preview?.controls_tag_stripped ?? 0}</strong> multi-tech control(s) will just lose this tag.</div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Reason (optional)</label>
                  <Textarea
                    rows={2}
                    value={retireNote}
                    onChange={(e) => setRetireNote(e.target.value)}
                    placeholder="e.g. Migrated off GitHub in Feb 2026"
                    data-testid="retire-tech-note"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">
                    Type <span className="font-semibold text-rose-600">retire</span> to confirm
                  </label>
                  <Input
                    value={retireConfirm}
                    onChange={(e) => setRetireConfirm(e.target.value)}
                    placeholder="retire"
                    autoComplete="off"
                    data-testid="retire-tech-confirm-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setConfirmingRetire(null); setRetireConfirm(''); }} data-testid="retire-tech-cancel">Cancel</Button>
              <Button
                onClick={handleRetireConfirm}
                disabled={busyTechId === confirmingRetire?.tech_id || !retireConfirmed}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="retire-tech-confirm"
              >
                {busyTechId === confirmingRetire?.tech_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                Retire technology
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
